import {
  Alert,
  Button,
  FormControl,
  FormHelperText,
  Stack,
  Theme,
  Typography,
  useTheme
} from '@mui/material'
import { useFormikContext } from 'formik'
import React from 'react'
import { Link } from 'react-router-dom'

import StatusCheckHeaders from 'components/rbac-settings/teams/StatusCheckHeaders'
import TestTeamExternalIntegration from 'components/rbac-settings/teams/TestTeamExternalIntegration'
import useAuth from 'hooks/use-auth'
import {
  authenticationStatus,
  ServiceGoldenSignal
} from 'types/api/services/golden-signal.d'

const ServiceGoldenSignalHeaders: React.FC<{
  authenticationError: string
  formPrefix?: string
  isAuthenticationSaved: boolean
  isCustom: boolean
  isIntegrationConfigured: boolean
  isSaveButtonDisabled: boolean
  onSaveAuthentication: () => void
  shouldShowAuthenticationButton: boolean
  statusCheckId?: string
}> = ({
  authenticationError,
  formPrefix,
  isAuthenticationSaved,
  isCustom,
  isIntegrationConfigured,
  isSaveButtonDisabled,
  onSaveAuthentication,
  shouldShowAuthenticationButton,
  statusCheckId
}) => {
  const form = useFormikContext<ServiceGoldenSignal>()
  const { currentTeamId } = useAuth()
  const theme = useTheme<Theme>()

  const showAlert = Boolean(authenticationError) || isAuthenticationSaved
  const alertSeverity =
    isAuthenticationSaved && !authenticationError ? 'success' : 'error'

  const handleLastAuthenticationStatus =
    (authStatus: authenticationStatus) => () =>
      form.setFieldValue('integration.lastAuthenticationStatus', authStatus)

  return (
    <Stack
      data-test-service-golden-signal-headers
      alignItems="flex-start"
      spacing={2}
      sx={{
        border: 1,
        borderColor: theme.palette.grey['400'],
        borderRadius: 1,
        padding: 2,
        width: '100%'
      }}
    >
      <FormControl component="fieldset" variant="standard">
        <Typography data-test-service-golden-signal-headers>
          Authentication and Headers
        </Typography>
        {isIntegrationConfigured ? (
          <FormHelperText data-test-service-golden-signal-headers-configured>
            These authentication and headers are shared across your team in{' '}
            <Link
              target="_blank"
              to={`/settings/team/${currentTeamId}/teamIntegrations`}
            >
              team integrations.
            </Link>
          </FormHelperText>
        ) : (
          <FormHelperText data-test-service-golden-signal-headers-unconfigured>
            We haven't seen this integration before and you might want to add an
            authentication key or header. Once added, it will be shared across
            your team.
          </FormHelperText>
        )}
      </FormControl>
      <StatusCheckHeaders
        formPrefix={formPrefix}
        isCustom={isCustom}
        isEditingDisabled={isAuthenticationSaved}
      />
      {shouldShowAuthenticationButton && (
        <>
          <TestTeamExternalIntegration
            authenticate={true}
            disabled={Boolean(form.errors?.integration?.headers) || !form.dirty}
            integration={form.values.integration}
            statusCheckId={statusCheckId}
            onError={handleLastAuthenticationStatus(
              authenticationStatus.UnAuthenticated
            )}
            onResponseReceived={handleLastAuthenticationStatus(
              authenticationStatus.Authenticated
            )}
          />
          <Button
            data-test-service-golden-signal-header-auth-button
            color="primary"
            disabled={isSaveButtonDisabled}
            type="submit"
            variant="contained"
            onClick={onSaveAuthentication}
          >
            Save Authentication
          </Button>
        </>
      )}
      {showAlert && (
        <Alert
          data-test-service-golden-signal-headers-alert={alertSeverity}
          severity={alertSeverity}
        >
          {alertSeverity === 'success' ? (
            <>
              Authentication has been saved to your team integration. It can be
              edited on the{' '}
              <Link to={`/settings/team/${currentTeamId}/teamIntegrations`}>
                team integrations{' '}
              </Link>
              page.
            </>
          ) : (
            authenticationError ||
            'There was an error while saving your Authentication. Please try again later.'
          )}
        </Alert>
      )}
    </Stack>
  )
}

export default ServiceGoldenSignalHeaders
